/**
 * Per-user page visit analytics storage (timeline).
 * Uses localStorage for now (keyed by userId). Timeline is logged-user + organization based.
 * When backend is ready: set USE_TIMELINE_BACKEND = true in services/analyticsApi.js and
 * set API_ENDPOINTS.TIMELINE_ANALYTICS in apiEndpoints.js to your GET/SAVE URLs.
 * Data is then loaded from and saved to the backend (interval + beforeunload).
 */

const STORAGE_KEY_PREFIX = "aq_page_analytics_";
const MAX_RECENT = 10;
const MAX_VISIT_HISTORY = 100;

/**
 * One completed visit (per date/time for timeline and reports).
 * @typedef {{ path: string, label: string, visitedAt: number, subtitle?: string, timeSpentMs: number, clickCount: number, loadTimeMs?: number, apiErrorCount?: number, networkErrorCount?: number }} VisitHistoryEntry
 */

/**
 * @param {string} userId - StaffId or unique user id
 * @returns {{ recentVisits: Array, pageStats: Record<string, { visitCount: number, totalTimeSpentMs: number, clickCount: number }>, visitHistory: VisitHistoryEntry[] }}
 */
export function getAnalytics(userId) {
  if (!userId) return { recentVisits: [], pageStats: {}, visitHistory: [] };
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + userId);
    if (!raw) return { recentVisits: [], pageStats: {}, visitHistory: [] };
    const data = JSON.parse(raw);
    return {
      recentVisits: Array.isArray(data.recentVisits) ? data.recentVisits.slice(0, MAX_RECENT) : [],
      pageStats: data.pageStats && typeof data.pageStats === "object" ? data.pageStats : {},
      visitHistory: Array.isArray(data.visitHistory) ? data.visitHistory.slice(0, MAX_VISIT_HISTORY) : [],
    };
  } catch {
    return { recentVisits: [], pageStats: {}, visitHistory: [] };
  }
}

/**
 * @param {string} userId
 * @param {{ recentVisits: Array, pageStats: Record, visitHistory?: VisitHistoryEntry[] }} data
 */
export function saveAnalytics(userId, data) {
  if (!userId) return;
  try {
    const payload = {
      recentVisits: (data.recentVisits || []).slice(0, MAX_RECENT),
      pageStats: data.pageStats || {},
      visitHistory: (data.visitHistory || []).slice(0, MAX_VISIT_HISTORY),
    };
    localStorage.setItem(STORAGE_KEY_PREFIX + userId, JSON.stringify(payload));
  } catch (e) {
    console.warn("analyticsStorage save failed", e);
  }
}

export { MAX_RECENT, MAX_VISIT_HISTORY };
